import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, CreditCard, Download, Plus, RefreshCw, Trash2 } from "lucide-react";
import { FormEvent, useMemo, useState } from "react";

import { Layout } from "@/components/Layout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { PaymentConfiguration, paymentConfigurationsApi } from "@/lib/api";
import { isPaymentConfigurationManagementEnabled } from "@/features/paymentConfigurationManagementFeature";

const displayValue = (value: string | number | null) => value ?? "—";

type SortKey = "pagamentoDynamics" | "pagamentoMagendo" | "diaPagamento" | "meses" | "$id";
type SortDirection = "asc" | "desc";

const emptyForm = {
  pagamentoDynamics: "",
  pagamentoMagendo: "",
  diaPagamento: "",
  meses: "",
};

type FormState = typeof emptyForm;

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === "string" && data) return data;
  if (data && typeof data === "object" && "message" in data) {
    return String((data as { message: unknown }).message);
  }
  if (data && typeof data === "object" && "Message" in data) {
    return String((data as { Message: unknown }).Message);
  }
  return fallback;
};

const formatPaymentStartMonth = (monthsOffset: number | null) => {
  if (monthsOffset === null) return "—";

  const currentDate = new Date();
  const paymentStartDate = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + monthsOffset,
    1,
  );

  return new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(paymentStartDate);
};

export default function PaymentConfigurations() {
  const [sortKey, setSortKey] = useState<SortKey>("pagamentoDynamics");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [configurationToDelete, setConfigurationToDelete] = useState<PaymentConfiguration | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await paymentConfigurationsApi.exportXlsx();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "configuracoes-pagamento.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Não foi possível exportar as configurações",
        description: getApiErrorMessage(error, "Tente novamente em instantes."),
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const {
    data: paymentConfigurations = [],
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["payment-configurations"],
    queryFn: paymentConfigurationsApi.getAll,
  });

  const createMutation = useMutation({
    mutationFn: paymentConfigurationsApi.create,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["payment-configurations"] });
      setIsCreateOpen(false);
      setForm(emptyForm);
      setFormError(null);
      toast({ title: "Configuração incluída", description: "A condição de pagamento foi cadastrada." });
    },
    onError: (error) => {
      setFormError(getApiErrorMessage(error, "Não foi possível incluir a configuração de pagamento."));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => paymentConfigurationsApi.remove(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["payment-configurations"] });
      setConfigurationToDelete(null);
      toast({ title: "Configuração excluída", description: "A condição de pagamento foi removida." });
    },
    onError: (error) => {
      setConfigurationToDelete(null);
      toast({
        title: "Erro ao excluir",
        description: getApiErrorMessage(error, "Não foi possível excluir a configuração de pagamento."),
        variant: "destructive",
      });
    },
  });

  const openCreateDialog = () => {
    setForm(emptyForm);
    setFormError(null);
    setIsCreateOpen(true);
  };

  const handleCreateSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const condition = form.pagamentoDynamics.trim();
    const paymentDay = form.diaPagamento.trim();
    const installments = Number(form.pagamentoMagendo);
    const months = Number(form.meses);

    if (!condition) return setFormError("Informe a condição no Dynamics.");
    if (condition.length > 50) return setFormError("A condição deve ter no máximo 50 caracteres.");
    if (paymentConfigurations.some((item) => item.pagamentoDynamics?.trim().toLowerCase() === condition.toLowerCase())) {
      return setFormError("Já existe uma configuração com essa condição.");
    }
    if (form.pagamentoMagendo === "" || !Number.isInteger(installments) || installments < 0) {
      return setFormError("Informe a quantidade de parcelas (número inteiro, 0 ou maior).");
    }
    if (!paymentDay) return setFormError("Informe o dia de pagamento.");
    if (paymentDay.length > 50) return setFormError("O dia de pagamento deve ter no máximo 50 caracteres.");
    if (form.meses === "" || !Number.isInteger(months) || months < 0) {
      return setFormError("Informe o mês de início (número inteiro, 0 ou maior).");
    }

    setFormError(null);
    createMutation.mutate({
      pagamentoDynamics: condition,
      pagamentoMagendo: installments,
      diaPagamento: paymentDay,
      meses: months,
    });
  };

  const sortedPaymentConfigurations = useMemo(
    () => [...paymentConfigurations].sort((first, second) => {
      const firstValue = first[sortKey];
      const secondValue = second[sortKey];

      if (firstValue === null) return secondValue === null ? 0 : 1;
      if (secondValue === null) return -1;

      const comparison = typeof firstValue === "number" && typeof secondValue === "number"
        ? firstValue - secondValue
        : String(firstValue).localeCompare(String(secondValue), "pt-BR", {
            sensitivity: "base",
            numeric: true,
          });

      return sortDirection === "asc" ? comparison : -comparison;
    }),
    [paymentConfigurations, sortDirection, sortKey],
  );

  const changeSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => current === "asc" ? "desc" : "asc");
      return;
    }

    setSortKey(key);
    setSortDirection("asc");
  };

  const sortIcon = (key: SortKey) => {
    if (sortKey !== key) return <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />;
    return sortDirection === "asc"
      ? <ArrowUp className="h-3.5 w-3.5" />
      : <ArrowDown className="h-3.5 w-3.5" />;
  };

  const sortableHeader = (key: SortKey, label: string, centered = false) => (
    <TableHead className={centered ? "text-center" : undefined}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={centered ? "mx-auto -my-1 h-8" : "-ml-3 -my-1 h-8"}
        onClick={() => changeSort(key)}
        aria-label={`Ordenar por ${label}`}
      >
        {label}
        {sortIcon(key)}
      </Button>
    </TableHead>
  );

  return (
    <Layout title="Configurações de Pagamento">
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <CreditCard className="h-5 w-5" />
            <p>Condições de pagamento recebidas do ERP e disponíveis para utilização no Portal de Pedidos.</p>
          </div>
          <div className="flex items-center gap-2">
            {isPaymentConfigurationManagementEnabled && (
              <Button type="button" onClick={openCreateDialog}>
                <Plus className="mr-2 h-4 w-4" />
                Nova configuração
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleExport()}
              disabled={isExporting || isLoading || paymentConfigurations.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? "Exportando..." : "Exportar"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
          </div>
        </div>

        <Card className="shadow-sm">
          <CardHeader className="border-b bg-muted/20">
            <div className="flex items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <CreditCard className="h-5 w-5 text-primary" />
                Configurações disponíveis
              </CardTitle>
              {!isLoading && !isError && (
                <Badge variant="outline">
                  {sortedPaymentConfigurations.length} configuraç{sortedPaymentConfigurations.length === 1 ? "ão" : "ões"}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="space-y-3 p-6" aria-label="Carregando configurações de pagamento">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : isError ? (
              <div className="p-8 text-center">
                <p className="text-sm font-medium text-destructive">
                  Não foi possível carregar as configurações de pagamento.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => void refetch()}
                  disabled={isFetching}
                >
                  Tentar novamente
                </Button>
              </div>
            ) : sortedPaymentConfigurations.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                Nenhuma configuração de pagamento disponível.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {sortableHeader("pagamentoDynamics", "Condição no Dynamics")}
                      {sortableHeader("pagamentoMagendo", "Quantidade de parcelas", true)}
                      {sortableHeader("diaPagamento", "Dia de pagamento")}
                      {sortableHeader("meses", "Mês de início do pagamento")}
                      {sortableHeader("$id", "Identificador")}
                      {isPaymentConfigurationManagementEnabled && (
                        <TableHead className="w-16 text-right">Ações</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPaymentConfigurations.map((configuration) => (
                      <TableRow key={configuration.$id ?? `${configuration.pagamentoDynamics}-${configuration.pagamentoMagendo}`}>
                        <TableCell className="font-medium">
                          {displayValue(configuration.pagamentoDynamics)}
                        </TableCell>
                        <TableCell className="text-center">
                          {displayValue(configuration.pagamentoMagendo)}
                        </TableCell>
                        <TableCell>{displayValue(configuration.diaPagamento)}</TableCell>
                        <TableCell className="capitalize">
                          {formatPaymentStartMonth(configuration.meses)}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {displayValue(configuration.$id)}
                        </TableCell>
                        {isPaymentConfigurationManagementEnabled && (
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              disabled={!configuration.$id}
                              onClick={() => setConfigurationToDelete(configuration)}
                              aria-label={`Excluir ${configuration.pagamentoDynamics ?? "configuração"}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground">
          As configurações são atualizadas pelo processo de integração com o ERP.
          {isPaymentConfigurationManagementEnabled &&
            " Inclusões e exclusões manuais feitas aqui valem imediatamente no Portal de Pedidos."}
        </p>
      </div>

      <Dialog
        open={isCreateOpen}
        onOpenChange={(open) => {
          if (!createMutation.isPending) setIsCreateOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleCreateSubmit} className="space-y-4" noValidate>
            <DialogHeader>
              <DialogTitle>Nova configuração de pagamento</DialogTitle>
              <DialogDescription>
                Cadastra uma nova condição de pagamento no Portal de Pedidos.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="pc-condition">Condição no Dynamics</Label>
              <Input
                id="pc-condition"
                maxLength={50}
                placeholder="Ex.: CNX5P_DEZ"
                value={form.pagamentoDynamics}
                onChange={(e) => setForm({ ...form, pagamentoDynamics: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pc-installments">Quantidade de parcelas</Label>
                <Input
                  id="pc-installments"
                  type="number"
                  min={0}
                  step={1}
                  value={form.pagamentoMagendo}
                  onChange={(e) => setForm({ ...form, pagamentoMagendo: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pc-months">Mês de início (meses a partir de hoje)</Label>
                <Input
                  id="pc-months"
                  type="number"
                  min={0}
                  step={1}
                  value={form.meses}
                  onChange={(e) => setForm({ ...form, meses: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pc-day">Dia de pagamento</Label>
              <Input
                id="pc-day"
                maxLength={50}
                placeholder="Ex.: 10 ou DIA 20"
                value={form.diaPagamento}
                onChange={(e) => setForm({ ...form, diaPagamento: e.target.value })}
              />
            </div>

            {formError && <p className="text-sm font-medium text-destructive">{formError}</p>}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateOpen(false)}
                disabled={createMutation.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Salvando..." : "Incluir"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={configurationToDelete !== null}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) setConfigurationToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir configuração de pagamento?</AlertDialogTitle>
            <AlertDialogDescription>
              A condição <strong>{configurationToDelete?.pagamentoDynamics}</strong> será removida. Clientes do plano
              customizado que a utilizam perderão esse vínculo, e um cliente que ficar sem nenhuma configuração não
              conseguirá parcelar até que uma nova seja selecionada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (configurationToDelete?.$id) deleteMutation.mutate(configurationToDelete.$id);
              }}
            >
              {deleteMutation.isPending ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
